import React, { useState } from 'react';
import { User, Bot, ExternalLink, ChevronDown, ChevronUp, Link2, Volume2, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Code block with copy button
function CodeBlock({ className, children }) {
  const [copied, setCopied] = useState(false);
  const lang = (className || '').replace('language-', '') || 'code';
  const code = String(children).replace(/\n$/, '');
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="my-3 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 group/code">
      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-900 px-4 py-1.5">
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{lang}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-white transition-colors"
        >
          {copied ? <><Check className="w-3 h-3 text-green-400" /> Kopyalandı</> : <><Copy className="w-3 h-3" /> Kopyala</>}
        </button>
      </div>
      <pre className="bg-gray-900 dark:bg-gray-950 px-4 py-3 overflow-x-auto">
        <code className="text-[13px] font-mono text-green-400 leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

export default function MessageItem({ message, onImageClick, onReplayAudio, hasAudio, isReplaying }) {
  const isUser = message.role === 'user';
  const [imageError, setImageError] = useState({});
  const [showSources, setShowSources] = useState(false);
  
  // Parse content for images and sources
  const parseContent = (content) => {
    if (!content) return { text: '', images: [], sources: [] };
    
    let text = content;
    let images = [];
    let sources = [];
    
    // Extract images
    const imageMatch = content.match(/\[IMAGES\](.*?)\[\/IMAGES\]/);
    if (imageMatch) {
      images = imageMatch[1].split('|').filter(url => url && url.trim());
      text = text.replace(/\[IMAGES\].*?\[\/IMAGES\]/, '').trim();
    }
    
    // Extract sources - multiple formats supported
    // Format 1: 📚 Kaynaklar:\n• url
    // Format 2: 📚 Kaynaklar: • url
    const sourcesRegex = /📚\s*Kaynaklar:?\s*([\s\S]*?)$/i;
    const sourcesMatch = text.match(sourcesRegex);
    
    if (sourcesMatch) {
      const sourceText = sourcesMatch[1];
      // Extract all URLs from the source text
      const urlRegex = /https?:\/\/[^\s\n]+/g;
      const urls = sourceText.match(urlRegex);
      if (urls) {
        sources = urls.map(url => url.replace(/[.,;:]+$/, '')); // Remove trailing punctuation
      }
      // Remove sources section from text
      text = text.replace(sourcesRegex, '').trim();
    }
    
    return { text, images, sources };
  };
  
  const { text, images, sources } = parseContent(message.content);
  
  // Format text with markdown support - IMPROVED
  const formatText = (text) => {
    if (!text) return null;
    
    // Split into lines
    const lines = text.split('\n');
    
    return lines.map((line, index) => {
      // Empty line = paragraph break
      if (!line.trim()) {
        return <div key={index} className="h-3" />;
      }
      
      // Check if it's a bullet point FIRST (before processing bold)
      const isBullet = line.trim().startsWith('•') || 
                       line.trim().startsWith('-') || 
                       (line.trim().startsWith('*') && !line.trim().startsWith('**'));
      
      if (isBullet) {
        let bulletContent = line.trim().replace(/^[•\-\*]\s*/, '');
        // Process bold in bullet content
        bulletContent = bulletContent.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
        
        return (
          <div key={index} className="flex items-start gap-2 ml-2 my-1">
            <span className="text-purple-500 dark:text-purple-400 mt-0.5 font-bold">•</span>
            <span className="flex-1" dangerouslySetInnerHTML={{ __html: bulletContent }} />
          </div>
        );
      }
      
      // Check if it's a header (line with **text:** pattern or starts with **)
      const headerMatch = line.match(/^\*\*(.*?):\*\*/) || (line.startsWith('**') && line.endsWith('**'));
      
      if (headerMatch) {
        let headerText = line.replace(/^\*\*/, '').replace(/\*\*$/, '');
        return (
          <div key={index} className="font-bold text-base text-gray-900 dark:text-white mt-4 mb-2">
            {headerText}
          </div>
        );
      }
      
      // Process markdown in regular lines
      let formattedLine = line;
      
      // Bold text: **text**
      formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');
      
      // Italic text: *text*
      formattedLine = formattedLine.replace(/(?<!\*)\*([^\*]+?)\*(?!\*)/g, '<em>$1</em>');
      
      // Code: `code`
      formattedLine = formattedLine.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-sm font-mono">$1</code>');
      
      // Regular line
      return (
        <div key={index} className="my-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: formattedLine }} />
      );
    });
  };
  
  return (
    <div className={`flex items-start space-x-3 ${
      isUser ? 'flex-row-reverse space-x-reverse' : ''
    }`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser 
          ? 'bg-gray-200 dark:bg-gray-700' 
          : 'bg-purple-500'
      }`}>
        {isUser ? (
          <User className="w-5 h-5 text-gray-700 dark:text-gray-300" />
        ) : (
          <Bot className="w-5 h-5 text-white" />
        )}
      </div>

      {/* Message Content */}
      <div className={`flex-1 ${
        isUser ? 'flex justify-end' : ''
      }`}>
        <div className={`inline-block max-w-4xl rounded-2xl overflow-hidden ${
          isUser
            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-tr-none'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-tl-none'
        }`}>
          
          {/* Images Grid - At Top */}
          {images.length > 0 && (
            <div className={`grid gap-1 ${
              images.length === 1 ? 'grid-cols-1' : 
              images.length === 2 ? 'grid-cols-2' : 
              'grid-cols-3'
            }`}>
              {images.map((imageUrl, index) => (
                !imageError[index] && (
                  <div 
                    key={index} 
                    className="relative group cursor-pointer overflow-hidden"
                    onClick={() => onImageClick && onImageClick(imageUrl)}
                  >
                    <img
                      src={imageUrl}
                      alt={`Görsel ${index + 1}`}
                      className="w-full h-40 object-cover transition-transform group-hover:scale-105"
                      onError={() => setImageError(prev => ({ ...prev, [index]: true }))}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
          
          {/* Text Content */}
          <div className="px-4 py-3">
            <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none relative">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Başlıklar — büyük, renkli, ikonlu
                  h1: ({node, ...props}) => <h1 className="text-xl font-extrabold text-gray-900 dark:text-white mt-5 mb-3 pb-2 border-b border-gray-200 dark:border-gray-600" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-[15px] font-bold text-gray-900 dark:text-white mt-4 mb-2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-[13px] font-bold text-purple-700 dark:text-purple-400 mt-3 mb-1.5" {...props} />,
                  // Paragraflar
                  p: ({node, ...props}) => <p className="my-2 leading-[1.75] text-gray-700 dark:text-gray-300" {...props} />,
                  // Sırasız liste
                  ul: ({node, ...props}) => <ul className="my-2.5 space-y-1.5 pl-1" {...props} />,
                  // Sıralı liste
                  ol: ({node, ...props}) => <ol className="my-2.5 space-y-1.5 pl-1 list-none counter-reset-item" {...props} />,
                  // Liste öğesi
                  li: ({node, ordered, index, ...props}) => (
                    <li className="flex items-start gap-2.5">
                      <span className={`mt-0.5 flex-shrink-0 font-bold text-xs ${ordered ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 w-5 h-5 rounded-full flex items-center justify-center' : 'text-purple-500 dark:text-purple-400'}`}>
                        {ordered ? `${(index || 0) + 1}` : '•'}
                      </span>
                      <span className="flex-1 text-gray-700 dark:text-gray-300" {...props} />
                    </li>
                  ),
                  // Kalın yazı
                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-900 dark:text-white" {...props} />,
                  // İtalik
                  em: ({node, ...props}) => <em className="italic text-gray-600 dark:text-gray-400" {...props} />,
                  // Kod — inline ve blok
                  code: ({node, inline, className, children, ...props}) => {
                    const isBlock = !inline && className;
                    if (isBlock) {
                      return <CodeBlock className={className}>{children}</CodeBlock>;
                    }
                    return <code className="px-1.5 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md text-[13px] font-mono border border-purple-200 dark:border-purple-800" {...props}>{children}</code>;
                  },
                  // Pre (kod bloğu wrapper)
                  pre: ({node, children, ...props}) => <>{children}</>,
                  // Linkler
                  a: ({node, ...props}) => <a className="text-blue-600 dark:text-blue-400 hover:underline font-medium" target="_blank" rel="noopener noreferrer" {...props} />,
                  // Yatay çizgi
                  hr: ({node, ...props}) => <hr className="my-4 border-gray-200 dark:border-gray-600" {...props} />,
                  // Blok alıntı
                  blockquote: ({node, ...props}) => <blockquote className="my-3 pl-4 border-l-3 border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20 py-2 pr-3 rounded-r-lg text-gray-700 dark:text-gray-300 italic" {...props} />,
                  // Tablo
                  table: ({node, ...props}) => <div className="my-3 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600"><table className="w-full text-sm" {...props} /></div>,
                  thead: ({node, ...props}) => <thead className="bg-gray-100 dark:bg-gray-800" {...props} />,
                  th: ({node, ...props}) => <th className="px-3 py-2 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600" {...props} />,
                  td: ({node, ...props}) => <td className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300" {...props} />,
                }}
              >
                {text}
              </ReactMarkdown>
              
              {/* Inline Replay Button - Positioned at the end of text content */}
              {!isUser && hasAudio && onReplayAudio && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReplayAudio(message.id);
                  }}
                  disabled={isReplaying}
                  className={`inline-flex items-center ml-1 p-0.5 rounded-full transition-all ${
                    isReplaying
                      ? 'text-purple-600 dark:text-purple-400 animate-pulse'
                      : 'text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400'
                  }`}
                  style={{ verticalAlign: 'middle', marginTop: '-2px' }}
                  title="Sesli cevabı tekrar dinle"
                  aria-label="Sesli cevabı tekrar dinle"
                >
                  <Volume2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            
            {/* Sources Button */}
            {sources.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setShowSources(!showSources)}
                  className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                    isUser 
                      ? 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <Link2 className="w-3.5 h-3.5" />
                  <span>Kaynaklar ({sources.length})</span>
                  {showSources ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </button>
                
                {showSources && (
                  <div className="mt-2 space-y-1.5">
                    {sources.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 text-xs truncate transition-colors ${
                          isUser 
                            ? 'text-blue-600 dark:text-blue-400 hover:underline' 
                            : 'text-blue-600 dark:text-blue-400 hover:underline'
                        }`}
                      >
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{new URL(url).hostname}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          
            {/* Timestamp */}
            {message.timestamp && (
              <p className={`text-[10px] mt-2 text-gray-400 dark:text-gray-500`}>
                {(() => {
                  const now = new Date();
                  const msgTime = new Date(message.timestamp);
                  const diffMs = now - msgTime;
                  const diffMin = Math.floor(diffMs / 60000);
                  const diffHr = Math.floor(diffMs / 3600000);
                  if (diffMin < 1) return 'az önce';
                  if (diffMin < 60) return `${diffMin} dk önce`;
                  if (diffHr < 24) return `${diffHr} saat önce`;
                  return msgTime.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) + ' ' + msgTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                })()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
